import type { Task, TaskOccurrence } from "@dashboard/api/task";
import { RRule } from "rrule";

export type TaskWithOccurrence = Task & {
    occurrence?: TaskOccurrence;
    dueDate: Date;
    isExpired: boolean;
};

export function withOccurrence(task: Task, occurrence?: TaskOccurrence): TaskWithOccurrence {
    const dueDate = occurrence?.scheduledAt
        ? new Date(occurrence.scheduledAt)
        : (getFirstRruleDate(task.rrule) ?? new Date(task.createdAt));

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDateDay = new Date(dueDate);
    dueDateDay.setHours(0, 0, 0, 0);
    const isExpired = !!occurrence && dueDateDay < today && occurrence.status !== "DONE";

    return {
        ...task,
        occurrence,
        dueDate,
        isExpired,
    };
}

function getFirstRruleDate(rrule: string | null): Date | undefined {
    if (!rrule) return undefined;
    try {
        return RRule.fromString(rrule).all((_, i) => i < 1)[0];
    } catch {
        return undefined;
    }
}

export function pickLatestOccurrence<T extends { scheduledAt: string | null }>(
    occurrences: readonly T[] | undefined,
): T | undefined {
    if (!occurrences || occurrences.length === 0) return undefined;
    let latest: T | undefined;
    let latestTime = -Infinity;
    for (const occurrence of occurrences) {
        const time = occurrence.scheduledAt ? new Date(occurrence.scheduledAt).getTime() : 0;
        if (Number.isNaN(time)) continue;
        if (!latest || time > latestTime) {
            latest = occurrence;
            latestTime = time;
        }
    }
    return latest;
}
